import React from "react";
import { Badge, Box, Grid, Stack, Typography } from "@mui/material";
import { EmailOutlined } from "@mui/icons-material";
import { Link } from "react-router";
import dayjs from "dayjs";
import { MinimalCase } from "@/src/types/Entities/MinimalCase";
import { Conversation } from "@/src/types/Entities/Messaging/Conversation";
import { conversationHasUnreadMessage, formatMessageDate } from "@/src/services/messagingService";
import { getProductData } from "@/src/services/productService";
import { getCaseHeadline } from "@/src/services/serviceWorldService";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import { messageCaseStyle } from "./messageCaseStyle";
import MessageCaseOpenButton from "./MessageCaseOpenButton";

type MessageCaseProps = {
  minimalCase: MinimalCase;
  conversations: Conversation[];
};

export default function MessageCase({ minimalCase, conversations }: MessageCaseProps) {
  const caseConversations = conversations.filter((conversation) => conversation.channel.caseId === minimalCase.id);
  const unreadConversations = caseConversations
    .filter(conversationHasUnreadMessage)
    .sort((a, b) => dayjs(b.latestMessageCreatedDate).valueOf() - dayjs(a.latestMessageCreatedDate).valueOf());
  const latestConversation = caseConversations.reduce<Conversation | null>(
    (latest, conversation) => (!latest || dayjs(conversation.latestMessageCreatedDate).isAfter(latest.latestMessageCreatedDate) ? conversation : latest),
    null,
  );

  return (
    <ContentBox
      headline={
        <Box component={"span"} sx={messageCaseStyle.headline}>
          {getCaseHeadline(minimalCase)}
          <Typography component={"span"}>
            {caseConversations.length} {caseConversations.length === 1 ? "Nachricht" : "Nachrichten"},{" "}
            {unreadConversations.length > 0 ? <strong>{unreadConversations.length} ungelesen</strong> : "0 ungelesen"}
          </Typography>
        </Box>
      }
      showDivider={true}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: "grow" }}>
          <Typography>
            {getProductData(minimalCase.name, "label")}
            {minimalCase.opposingParty ? ` | ${minimalCase.opposingParty}` : ""}
          </Typography>
          <Typography>Fallnummer: {minimalCase.reference}</Typography>
          <Typography sx={{ marginBottom: unreadConversations.length > 0 ? "1rem" : "0rem" }}>
            Letzte Nachricht: {latestConversation ? formatMessageDate(latestConversation.latestMessageCreatedDate) : "-"}
          </Typography>
        </Grid>
        {unreadConversations.length === 0 && (
          <Grid size={{ xs: 12, sm: "auto" }} sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-end" }, alignItems: "flex-end" }}>
            <MessageCaseOpenButton minimalCase={minimalCase} />
          </Grid>
        )}
      </Grid>
      {unreadConversations.length > 0 && (
        <Stack spacing={{ xs: 1.5, sm: 0 }}>
          {unreadConversations.map((conversation) =>
            conversation.id ? (
              <Box
                key={conversation.id}
                component={Link}
                to={`/servicewelt/nachrichten/fall/${minimalCase.id}/konversation/${conversation.id}`}
                sx={messageCaseStyle.unreadConversation}
              >
                <Badge variant={"dot"} color={"warning"}>
                  <EmailOutlined />
                </Badge>
                <Typography noWrap sx={messageCaseStyle.subject}>
                  <strong>{conversation.subject}</strong>
                </Typography>
                <Typography sx={messageCaseStyle.date}>{formatMessageDate(conversation.latestMessageCreatedDate)}</Typography>
              </Box>
            ) : null,
          )}
        </Stack>
      )}
      {unreadConversations.length > 0 && (
        <Stack direction={"row"} sx={messageCaseStyle.actions}>
          <MessageCaseOpenButton minimalCase={minimalCase} />
        </Stack>
      )}
    </ContentBox>
  );
}
